/**
 * iter 72 (Phase C+D) — Organization Unification Engine — Settings UI
 *
 * Mounted from Settings → My Organization → "Org Unification" (super_admin only).
 * Two side-by-side cards:
 *   • OUTBOUND  (this org generates a Merge Key → some other org will consume it)
 *   • INBOUND   (this org consumes someone else's Merge Key)
 *
 * Inbound flows through `ReviewMergeDrawer` (per-user collisions, role/dept
 * mapping, submit-for-platform-approval).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Key, Copy, ArrowsLeftRight, Trash, WarningCircle,
} from "@phosphor-icons/react";
import { api, formatErr } from "../lib/api";
import { useBrand } from "./AppShell";
import ReviewMergeDrawer from "./ReviewMergeDrawer";

/* ========================================================================= */
/*  Outbound card — generate/revoke key                                       */
/* ========================================================================= */
function OutboundCard() {
  const { platformName } = useBrand();
  const [active, setActive] = useState(null);   // null | key obj
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("acquisition");
  const [busy, setBusy] = useState(false);
  const [justGenerated, setJustGenerated] = useState(null);  // includes token

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/unify/keys/active");
      setActive(r.data || null);
    } catch (e) { /* silent on 403 — non-super_admin */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onGenerate = async () => {
    if (!confirm(
      `Generate a ${mode.toUpperCase()} merge key?\n\n` +
      `This key allows a Super Admin of another org to absorb THIS organization's data.\n` +
      `Key expires in 24 hours. You can revoke it any time before it's consumed.`
    )) return;
    setBusy(true);
    try {
      const r = await api.post("/unify/keys", { mode });
      setJustGenerated(r.data);
      setActive({ ...r.data, consumed_at: null, revoked_at: null });
      toast.success("Merge key generated.");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const onRevoke = async () => {
    if (!active) return;
    if (!confirm("Revoke this merge key? Anyone who has it will no longer be able to use it.")) return;
    setBusy(true);
    try {
      await api.delete(`/unify/keys/${active.id}`);
      setActive(null);
      setJustGenerated(null);
      toast.success("Key revoked.");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const onCopy = async (token) => {
    try { await navigator.clipboard.writeText(token); toast.success("Copied."); }
    catch { toast.error("Copy failed — select and copy manually."); }
  };

  return (
    <div className="surface p-6" data-testid="unify-outbound-card">
      <div className="flex items-center gap-3 mb-3">
        <ArrowsLeftRight size={20} weight="bold" />
        <div>
          <div className="label-xs">OUTBOUND</div>
          <h3 className="heading text-lg">This org → another org</h3>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Generate a Merge Key to allow another {platformName} organization to absorb THIS org&apos;s data.
        Choose <strong>Cleanup</strong> if both orgs are the same company being consolidated, or
        <strong> Acquisition</strong> if another company has acquired this one.
      </p>

      {loading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : active ? (
        <div className="space-y-3" data-testid="unify-outbound-active-key">
          <div className="surface p-3 bg-amber-50/40">
            <div className="label-xs mb-1">ACTIVE KEY</div>
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono break-all">
                {justGenerated?.token || active.token || "•••••• (token hidden after generation)"}
              </code>
              {(justGenerated?.token || active.token) && (
                <button
                  className="btn btn-xs btn-ghost"
                  onClick={() => onCopy(justGenerated?.token || active.token)}
                  data-testid="unify-outbound-copy-btn"
                  title="Copy"
                >
                  <Copy size={12} />
                </button>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              Mode: <strong className="uppercase">{active.mode}</strong>
              {" · "}Expires: {active.expires_at ? new Date(active.expires_at).toLocaleString() : "—"}
            </div>
            {justGenerated && (
              <div className="text-xs text-amber-700 mt-2 flex items-start gap-1">
                <WarningCircle size={14} weight="duotone" className="mt-px shrink-0" />
                <span>
                  This is the ONLY time the full key will be shown. Copy it now — you can&apos;t
                  retrieve it later (we&apos;ll only show a partial). Share via secure channel
                  (DM, password manager) — not email.
                </span>
              </div>
            )}
          </div>
          <button
            className="btn btn-destructive"
            onClick={onRevoke}
            disabled={busy}
            data-testid="unify-outbound-revoke-btn"
          >
            <Trash size={14} /> REVOKE KEY
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <div className="label-xs mb-1">MODE</div>
            <div className="flex gap-1">
              {[
                { v: "cleanup",      label: "Cleanup" },
                { v: "acquisition",  label: "Acquisition" },
              ].map(o => (
                <button
                  key={o.v}
                  className={`btn btn-sm ${mode === o.v ? "btn-primary" : "btn-ghost"}`}
                  onClick={() => setMode(o.v)}
                  data-testid={`unify-outbound-mode-${o.v}`}
                >{o.label}</button>
              ))}
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={onGenerate}
            disabled={busy}
            data-testid="unify-outbound-generate-btn"
          >
            <Key size={14} /> GENERATE MERGE KEY
          </button>
        </div>
      )}
    </div>
  );
}

/* ========================================================================= */
/*  Inbound card — apply someone else's key                                   */
/* ========================================================================= */
function InboundCard() {
  const { platformName } = useBrand();
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const onPreview = async () => {
    if (!token.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/unify/keys/${encodeURIComponent(token.trim())}/preview`);
      setPreview(r.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="surface p-6" data-testid="unify-inbound-card">
      <div className="flex items-center gap-3 mb-3">
        <ArrowsLeftRight size={20} weight="bold" className="rotate-180" />
        <div>
          <div className="label-xs">INBOUND</div>
          <h3 className="heading text-lg">Another org → this org</h3>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Enter a Merge Key generated by another {platformName} organization to absorb their data
        into this one. After you submit, a {platformName} platform reviewer will verify the
        merge before it executes.
      </p>

      <div className="space-y-3">
        <div>
          <div className="label-xs mb-1">MERGE KEY</div>
          <input
            className="input mono uppercase"
            value={token}
            onChange={(e) => setToken(e.target.value.toUpperCase().replace(/\s+/g, ""))}
            placeholder="Paste the 32-char key"
            spellCheck={false}
            autoComplete="off"
            data-testid="unify-inbound-token-input"
          />
        </div>
        <button
          className="btn btn-primary"
          onClick={onPreview}
          disabled={busy || token.length < 16}
          data-testid="unify-inbound-preview-btn"
        >
          <Key size={14} /> VALIDATE & PREVIEW
        </button>
      </div>

      {preview && (
        <ReviewMergeDrawer
          preview={preview}
          token={token.trim()}
          onClose={() => setPreview(null)}
          onSubmitted={() => { setPreview(null); setToken(""); }}
        />
      )}
    </div>
  );
}

/* ========================================================================= */
/*  Public component                                                          */
/* ========================================================================= */
export default function UnifyEngineSection() {
  // Visibility gate: try GET /unify/keys/active. If it 403s the user is
  // not a super_admin — hide silently. Otherwise show the section.
  const [allowed, setAllowed] = useState(null);   // null = checking
  useEffect(() => {
    let cancelled = false;
    api.get("/unify/keys/active")
      .then(() => { if (!cancelled) setAllowed(true); })
      .catch(() => { if (!cancelled) setAllowed(false); });
    return () => { cancelled = true; };
  }, []);
  if (allowed !== true) return null;

  return (
    <section className="space-y-4" data-testid="unify-engine-section">
      <div className="flex items-center gap-3">
        <Key size={20} weight="duotone" />
        <div>
          <div className="label-xs">ORG UNIFICATION</div>
          <h2 className="heading text-xl">Merge orgs — Cleanup or Acquisition</h2>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Combine two organizations into one. <strong>Cleanup</strong> assumes both orgs
        are the same company being consolidated (Super Admins from the source keep
        Super Admin status). <strong>Acquisition</strong> assumes the source has been
        acquired by this org — source Super Admins are downgraded to Admin.
        <br />
        <strong className="text-destructive">This operation is final.</strong>{" "}
        Run a dry-run via the Platform Developer portal first to gain confidence.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <OutboundCard />
        <InboundCard />
      </div>
      <RecentMigrationsList />
    </section>
  );
}


/* ========================================================================= */
/* Phase F — Recent migrations involving this org. Each row offers a         */
/* "Download receipt" button if the migration is in a terminal state.       */
/* ========================================================================= */
function RecentMigrationsList() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get("/unify/jobs/mine")
      .then(r => { if (!cancelled) setRows(r.data || []); })
      .catch(() => { /* 403 = not super_admin → silent (parent already gated) */ });
    return () => { cancelled = true; };
  }, []);

  if (rows.length === 0) return null;

  const downloadReceipt = (jobId) => {
    api.get(`/unify/jobs/${jobId}/receipt`, { responseType: "blob" })
      .then(resp => {
        const url = URL.createObjectURL(resp.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `migration-receipt-${jobId.slice(0, 8)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(e => toast.error(formatErr(e)));
  };

  return (
    <section className="surface p-6 mt-4" data-testid="unify-recent-migrations">
      <div className="label-xs mb-2">RECENT MIGRATIONS INVOLVING THIS ORG</div>
      <table className="dense">
        <thead><tr><th>WHEN</th><th>ROLE</th><th>OTHER ORG</th><th>MODE</th><th>STATUS</th><th>RECEIPT</th></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} data-testid={`unify-recent-row-${r.id}`}>
              <td className="text-xs">{r.submitted_at ? new Date(r.submitted_at).toLocaleString() : "—"}</td>
              <td className="uppercase mono text-xs">{r.role}</td>
              <td>{r.role === "source" ? r.target_name : r.source_name}</td>
              <td className="uppercase mono text-xs">{r.mode}</td>
              <td className="uppercase mono text-xs">{r.status}</td>
              <td>
                {["completed", "aborted"].includes(r.status) && (
                  <button
                    className="btn btn-secondary btn-xs"
                    onClick={() => downloadReceipt(r.id)}
                    data-testid={`unify-recent-receipt-btn-${r.id}`}
                  >
                    Download
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
